import React, { ReactElement } from 'react';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { CircularProgress, SxProps } from '@mui/material';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { StyledButton, StyledIconButton } from './styles';

export interface IButton {
  id?: string;
  label?: string;
  icon?: EIconTypes;
  endIcon?: boolean;
  iconFontSize?: number;
  variant?:
    'text' | 'outlined' | 'contained' | undefined | EButtonVariants.outlinedIconTertiary;
  styleVariant?: 'success' | 'error';
  onClick?: (...args: any) => void;
  onMiddleClick?: (...args: any) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large' | undefined;
  tableButton?: boolean;
  sizePX?: EButtonSizesPX;
  className?: string;
  isLoading?: boolean;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
  sx?: SxProps;
  permission?: EPermission | EPermission[];
  permissionOr?: boolean;
  isUploadButton?: boolean;
  uploadButtonAcceptExtensions?: string;
  isMultiple?: boolean;
  alignWithInputSX?:
    | boolean
    | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number; columns?: number };
}

const Button = ({
  id,
  label,
  icon,
  endIcon = false,
  iconFontSize,
  onClick,
  onMiddleClick,
  disabled = false,
  variant = EButtonVariants.contained,
  styleVariant,
  size = EButtonSizes.medium,
  tableButton = false,
  sizePX = EButtonSizesPX.px24,
  className = '',
  isLoading = false,
  fullWidth = false,
  type = 'button',
  sx,
  permission,
  permissionOr,
  isUploadButton = false,
  uploadButtonAcceptExtensions = '*',
  isMultiple = false,
  alignWithInputSX = false,
}: IButton): ReactElement | null => {
  const { hasPermissions } = usePermission();

  const getAlignmentSx = () => {
    if (!alignWithInputSX) return sx;

    if (typeof alignWithInputSX === 'boolean') {
      return {
        marginTop: { sm: '0px', md: '28px' },
        ...sx,
      };
    }

    const { xs = 12, sm = 12, md = 3, lg, xl, columns = 12 } = alignWithInputSX;
    const marginTop: any = {};

    if (xs < columns) marginTop.xs = '28px';
    if (sm < columns) marginTop.sm = '28px';
    if (md < columns) marginTop.md = '28px';
    if (lg !== undefined && lg < columns) marginTop.lg = '28px';
    if (xl !== undefined && xl < columns) marginTop.xl = '28px';

    return {
      marginTop,
      ...sx,
    };
  };

  const enhancedSx = getAlignmentSx();

  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    if (event.button === 1) {
      event.preventDefault();
    }
  };

  const handleAuxClick = onMiddleClick
    ? (event: React.MouseEvent<HTMLElement>) => {
        if (event.button === 1) {
          event.preventDefault();
          onMiddleClick(event);
        }
      }
    : undefined;

  if (permission && !hasPermissions(permission, permissionOr)) {
    return null;
  }

  return label ? (
    <StyledButton
      className={className}
      size={size}
      variant={variant}
      styleVariant={styleVariant}
      {...(!isUploadButton
        ? { onClick, onAuxClick: handleAuxClick, onMouseDown: handleMouseDown }
        : {})}
      disabled={disabled}
      disableElevation={true}
      disableRipple={true}
      startIcon={icon && !endIcon && !isLoading ? <Icon type={icon} /> : null}
      endIcon={icon && endIcon && !isLoading ? <Icon type={icon} /> : null}
      isLoading={isLoading}
      fullWidth={fullWidth}
      type={type}
      sx={enhancedSx}
      {...(!isUploadButton ? { id } : {})}
      {...(isUploadButton ? { component: 'label' } : {})}
    >
      {isLoading ? <CircularProgress size={20} /> : <span>{label}</span>}
      {isUploadButton && (
        <input
          id={id}
          hidden
          accept={uploadButtonAcceptExtensions}
          multiple={isMultiple}
          type="file"
          onChange={(e) => {
            if (onClick) {
              onClick(e.target.files);
              e.target.value = '';
            }
          }}
        />
      )}
    </StyledButton>
  ) : (
    <StyledIconButton
      className={className}
      variant={variant}
      styleVariant={styleVariant}
      disabled={disabled || isLoading}
      onClick={onClick}
      onAuxClick={handleAuxClick}
      onMouseDown={handleMouseDown}
      tableButton={tableButton}
      sizePX={sizePX}
      disableRipple={true}
      sx={enhancedSx}
      id={id}
    >
      {isLoading ? (
        <CircularProgress size={16} />
      ) : (
        icon && <Icon type={icon} fontSize={iconFontSize} />
      )}
    </StyledIconButton>
  );
};

export default Button;
